import { isPortableSetting } from '@hoedokloop/domain';
import { IndexedDbStudyRepository } from './repository';
import { EXPORT_SCHEMA_VERSION, type ExportData } from '@hoedokloop/contracts';
import { encryptBackup, type BackupId, type RecoverySecret } from '@hoedokloop/backup-crypto';
import type { Instant, PhotoAsset, StudyStoreName, UUID } from '@hoedokloop/domain';
import type { StudyFixture } from '@hoedokloop/domain/fixtures';
import type { StudyRepository } from '@hoedokloop/platform-ports';

import { createExportArchive, readExportArchive, type ExportArchiveSource } from './archive';
import { materializeArchiveData, type CheckpointSealer } from './import-service';

const studyStores: StudyStoreName[] = [
  'exams',
  'subjects',
  'studyLogs',
  'wrongNotes',
  'photoAssets',
  'plans',
  'planItems',
  'milestones',
  'mogakSessions',
  'mockScores',
  'noteInbox',
];

export class PortabilityReadError extends Error {
  readonly store: StudyStoreName;

  constructor(store: StudyStoreName) {
    super(`Failed to read ${store} for portable archive`);
    this.name = 'PortabilityReadError';
    this.store = store;
  }
}

export async function snapshotStudyRepository(repository: StudyRepository): Promise<StudyFixture> {
  const entries = await Promise.all(
    studyStores.map(async (store) => {
      const result = await repository.list(store, { includeDeleted: true });
      if (!result.ok) throw new PortabilityReadError(store);
      return result.value;
    }),
  );
  return {
    ...Object.fromEntries(studyStores.map((store, index) => [store, entries[index] ?? []])),
    userSettings:
      repository instanceof IndexedDbStudyRepository
        ? (await repository.database.settings.toArray()).filter((item) =>
            isPortableSetting(item.key),
          )
        : [],
  } as StudyFixture;
}

async function archiveSourceFromFixture(options: {
  fixture: StudyFixture;
  exportedAt: Instant;
  appVersion: string;
}): Promise<ExportArchiveSource> {
  const photoFiles: Record<string, Uint8Array> = {};
  const photoAssets = await Promise.all(
    options.fixture.photoAssets.map(async (photo: PhotoAsset) => {
      const extension =
        photo.mimeType === 'image/png' ? 'png' : photo.mimeType === 'image/jpeg' ? 'jpg' : 'webp';
      const originalPath = `photos/${photo.id}.${extension}`;
      const thumbnailPath = `photos/${photo.id}.thumb.${extension}`;
      photoFiles[originalPath] = new Uint8Array(await photo.original.arrayBuffer());
      photoFiles[thumbnailPath] = new Uint8Array(await photo.thumbnail.arrayBuffer());
      return {
        id: photo.id,
        originalPath,
        thumbnailPath,
        mimeType: photo.mimeType,
        width: photo.width,
        height: photo.height,
        byteLength: photo.byteLength,
        sha256: photo.sha256,
        createdAt: photo.createdAt,
        ...(photo.deletedAt === undefined ? {} : { deletedAt: photo.deletedAt }),
      };
    }),
  );
  const data: ExportData = {
    schemaVersion: options.fixture.exams.some((exam) => !exam.examDate) ? EXPORT_SCHEMA_VERSION : 2,
    exams: options.fixture.exams,
    subjects: options.fixture.subjects,
    studyLogs: options.fixture.studyLogs,
    wrongNotes: options.fixture.wrongNotes,
    photoAssets,
    plans: options.fixture.plans,
    planItems: options.fixture.planItems,
    milestones: options.fixture.milestones,
    mogakSessions: options.fixture.mogakSessions,
    mockScores: options.fixture.mockScores ?? [],
    noteInbox: options.fixture.noteInbox ?? [],
    userSettings: options.fixture.userSettings ?? [],
  };
  return {
    appVersion: options.appVersion,
    exportedAt: options.exportedAt,
    data,
    photoFiles,
  };
}

export async function createPortableArchive(options: {
  repository: StudyRepository;
  exportedAt: Instant;
  appVersion: string;
}): Promise<Uint8Array> {
  return createExportArchive(
    await archiveSourceFromFixture({
      fixture: await snapshotStudyRepository(options.repository),
      exportedAt: options.exportedAt,
      appVersion: options.appVersion,
    }),
  );
}

/** The recovery path supplies a native readonly snapshot to the same ZIP serializer. */
export async function createPortableArchiveFromFixture(options: {
  fixture: StudyFixture;
  exportedAt: Instant;
  appVersion: string;
}): Promise<Uint8Array> {
  return createExportArchive(await archiveSourceFromFixture(options));
}

export function createEncryptedCheckpointSealer(options: {
  backupId: BackupId;
  encryptionKey: RecoverySecret;
  now: () => Instant;
  createId: () => UUID;
  appVersion: string;
}): CheckpointSealer {
  return {
    seal: async (fixture) => {
      const createdAt = options.now();
      const snapshotId = options.createId();
      const source = await archiveSourceFromFixture({
        fixture,
        exportedAt: createdAt,
        appVersion: options.appVersion,
      });
      const archive = await createExportArchive(source);
      const envelope = await encryptBackup({
        plaintext: archive,
        encryptionKey: options.encryptionKey,
        header: {
          backupId: options.backupId,
          createdAt,
          schemaVersion: source.data.schemaVersion,
          snapshotId,
        },
      });
      return new Blob([JSON.stringify(envelope)], {
        type: 'application/vnd.hoedokloop.checkpoint+json',
      });
    },
  };
}

export async function parsePortableArchive(bytes: Uint8Array): Promise<{
  fixture: StudyFixture;
  manifest: Awaited<ReturnType<typeof readExportArchive>>['manifest'];
}> {
  const parsed = await readExportArchive(bytes);
  return { fixture: materializeArchiveData(parsed), manifest: parsed.manifest };
}
